import { useState } from "react";
import { generateOtp } from "@services/authenticationService";
import { environment } from "@environments/environment";
import { useLoginGateway } from "@hooks/store";
import { buildGenerateOtpPayload } from "./loginPayloads";
import { useInvisibleRecaptcha } from "./useInvisibleRecaptcha";
import type { PhoneCredentials } from "./types";

type UseSendOtpOptions = {
  phone: PhoneCredentials;
  enabled?: boolean;
  /** Admission flow only — omitted for mobile login */
  admissionNumber?: string;
  onOtpSent?: () => void;
};

export function useSendOtp({
  phone,
  enabled = true,
  admissionNumber,
  onOtpSent,
}: UseSendOtpOptions) {
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const loginGateway = useLoginGateway();

  const sendOtpWithToken = async (
    captchaToken: string,
    resetCaptcha: () => void
  ) => {
    setIsSending(true);
    try {
      const payload = buildGenerateOtpPayload(
        phone,
        captchaToken,
        admissionNumber
      );
      const res = await generateOtp(payload, loginGateway);
      if (res) {
        resetCaptcha();
        setError("");
        onOtpSent?.();
      }
    } catch (err: unknown) {
      resetCaptcha();
      const message =
        (err as { detail?: string; message?: string })?.detail ||
        (err as { message?: string })?.message ||
        "Failed to send OTP";
      setError(message);
    } finally {
      setIsSending(false);
    }
  };

  const { captchaRef, executeCaptcha, resetCaptcha } = useInvisibleRecaptcha({
    siteKey: environment.captchaSiteKey,
    onToken: (token) => sendOtpWithToken(token, resetCaptcha),
    executeOnMount: false,
    enabled,
  });

  const requestOtp = () => executeCaptcha();

  return {
    error,
    setError,
    isSending,
    requestOtp,
    captchaRef,
  };
}
